import { createContext, useContext, useState, type CSSProperties } from 'react';
import {
  CalendarIcon,
  ClockIcon,
  CopyIcon,
  MicIcon,
  PhoneOffIcon,
  PlusIcon,
  ShieldCheckIcon,
  SquareArrowOutUpRightIcon,
  VideoIcon,
} from 'lucide-react';
import { Layout } from '../components/Layout.js';
import { useAuth } from '../auth/AuthProvider.js';
import { KsyosEnv } from './EhealthKsyos.js';
import { TherapielandEnv } from './EhealthTherapieland.js';

/**
 * Sales-etalage van het e-health-integratieconcept (GitHub #11), voor
 * Therapieland en Ksyos.
 *
 * Kernprincipe: SUPPOHRT is alleen de container met een beveiligde doorlink.
 * ALLE patiëntcontext (cliëntkeuze, programma's, vragenlijsten, videoconsult)
 * leeft binnen de omgeving van de aanbieder in de viewport, 'omgeving in een
 * omgeving'. De SUPPOHRT-schil eromheen bevat bewust geen enkel patiëntgegeven.
 *
 * Beide aanbieders hebben een eigen, naar screenshots nagebouwde omgeving met
 * eigen navigatie: EhealthKsyos.tsx en EhealthTherapieland.tsx. Wat ze delen is
 * de programma-catalogus; Therapieland draait op Ksyos, zelfde moederbedrijf.
 *
 * Beeldbellen is de enige uitzondering: die module stond in geen van beide
 * gescreenshotte accounts aan, dus daarvoor leeft hier nog het eigen
 * referentie-ontwerp. Therapieland toont dat in zijn Beeldbellen-module.
 *
 * Alle data in de viewport is fictief en hardcoded; er zijn geen externe calls.
 * De enige externe verwijzing is de 'Openen in nieuw venster'-link.
 */

interface Client {
  id: string;
  naam: string;
  leeftijd: number;
  traject: string;
  actief: boolean;
}

// Fictieve cliënt: leeft binnen de omgeving van de aanbieder, niet in SUPPOHRT.
const DEMO_CLIENT: Client = {
  id: 'voorbeeld',
  naam: 'Mw. S. van der Voorbeeld',
  leeftijd: 34,
  traject: 'Angst & piekeren',
  actief: true,
};

type BrandKey = 'therapieland' | 'ksyos';

interface Brand {
  key: BrandKey;
  naam: string;
  loginUrl: string;
  /** Deel-URL van het videoconsult, getoond in het beeldbelscherm. */
  deelUrl: string;
  /** Merkkleur voor tekst en gevulde vlakken; moet wit contrasteren (>= 4.5:1). */
  accent: string;
  tint: string;
  button: string;
  banner: string;
  /** Succes-actie (versturen, kopiëren). Generiek, niet merkgebonden. */
  success: string;
}

// Kleuren zijn gesampled uit docs/designs/screenshots-therapieland/ en
// docs/designs/screenshots-ksyos/, niet uit de logo's: het Ksyos-logoroze
// (#ff37f0) haalt maar 3:1 tegen wit en komt in het portaal nergens voor.
const BRANDS: Record<BrandKey, Brand> = {
  therapieland: {
    key: 'therapieland',
    naam: 'Therapieland',
    loginUrl: 'https://app.therapieland.nl/login',
    deelUrl: 'https://mijn.therapieland.nl/video-call/start/…',
    accent: '#086d93',
    tint: '#e1edf2',
    button: '#086d93',
    banner: '#086d93',
    success: '#3aa935',
  },
  ksyos: {
    key: 'ksyos',
    naam: 'Ksyos',
    loginUrl: 'https://connect.ksyos.com/',
    deelUrl: 'https://connect.ksyos.com/…',
    accent: '#e2176c',
    tint: '#fce9f1',
    button: '#a3104e',
    banner: '#e2176c',
    success: '#3aa935',
  },
};

const BrandContext = createContext<Brand>(BRANDS.therapieland);
const useBrand = (): Brand => useContext(BrandContext);

function initials(naam: string): string {
  const parts = naam
    .replace(/^(Mw\.|Dhr\.)\s*/, '')
    .trim()
    .split(/\s+/);
  return ((parts[0]?.[0] ?? '') + (parts[parts.length - 1]?.[0] ?? '')).toUpperCase();
}

export function EhealthIntegratiePage(): JSX.Element {
  const { user } = useAuth();
  return user?.demo === true ? <EhealthEtalage /> : <EhealthDoorlinks />;
}

/** Live alleen de SSO-doorlink; de nagebouwde omgeving is demo-materiaal (besluit Jos 20 okt 2026). */
function EhealthDoorlinks(): JSX.Element {
  return (
    <Layout>
      <div className="mx-auto max-w-2xl p-6">
        <p className="mb-5 text-sm text-ink-500">
          Open de omgeving van de aanbieder in een nieuw venster. Je logt daar in met je eigen
          account; er gaan geen patiëntgegevens via de Kennishub.
        </p>
        <div className="grid gap-3 sm:grid-cols-2">
          {Object.values(BRANDS).map((b) => (
            <a
              key={b.key}
              href={b.loginUrl}
              target="_blank"
              rel="noreferrer"
              className="flex items-center justify-between rounded-xl border border-brand-100 bg-white px-5 py-4 font-semibold transition-colors hover:bg-brand-50"
              style={{ color: b.accent }}
            >
              {b.naam} openen
              <SquareArrowOutUpRightIcon size={16} />
            </a>
          ))}
        </div>
      </div>
    </Layout>
  );
}

function EhealthEtalage(): JSX.Element {
  const [brandKey, setBrandKey] = useState<BrandKey>('therapieland');
  const brand = BRANDS[brandKey];

  return (
    <Layout>
      <BrandContext.Provider value={brand}>
        {/* De merkkleur als CSS-variabele, zodat Tailwind-focusklassen meekleuren. */}
        <div
          className="flex h-full min-h-0 flex-col bg-white"
          style={{ '--brand': brand.accent } as CSSProperties}
        >
          {/* Slanke SUPPOHRT-contextbalk: geen patiëntdata, alleen de kadering. */}
          <div className="flex flex-wrap items-center justify-between gap-2 border-b border-brand-100 bg-white px-5 py-2.5">
            <div className="flex items-center gap-2.5">
              <div className="flex items-center gap-1">
                {Object.values(BRANDS).map((b) => {
                  const on = b.key === brandKey;
                  return (
                    <button
                      key={b.key}
                      type="button"
                      onClick={() => setBrandKey(b.key)}
                      className="rounded-md px-2.5 py-1 text-[13px] font-semibold transition-colors"
                      style={
                        on
                          ? { backgroundColor: b.tint, color: b.accent }
                          : { color: 'rgb(107 122 138 / 0.75)' }
                      }
                    >
                      {b.naam}
                    </button>
                  );
                })}
              </div>
              <span className="rounded-full border border-mosterdgeel-500/30 bg-mosterdgeel-10 px-2 py-0.5 text-[10px] font-semibold text-[#7e5a1c]">
                Demo
              </span>
            </div>
            <div className="flex items-center gap-4">
              <span className="inline-flex items-center gap-1.5 rounded-full bg-[#eaf6f0] px-2.5 py-1 text-[11px] font-semibold text-[#2c8a5f]">
                <ShieldCheckIcon size={13} /> Geen patiëntgegevens in SUPPOHRT
              </span>
              <a
                href={brand.loginUrl}
                target="_blank"
                rel="noreferrer"
                className="inline-flex items-center gap-1.5 text-[12px] font-medium text-ink-500/70 transition-colors hover:text-ink-700"
              >
                <SquareArrowOutUpRightIcon size={14} /> Openen in nieuw venster
              </a>
            </div>
          </div>

          {brandKey === 'ksyos' ? (
            <KsyosEnv onNaarTherapieland={() => setBrandKey('therapieland')} />
          ) : (
            <TherapielandEnv beeldbellen={<BeeldbellenSlot />} />
          )}
        </div>
      </BrandContext.Provider>
    </Layout>
  );
}

/** Beeldbellen kent twee stappen: het startformulier en het gesprek zelf. */
function BeeldbellenSlot(): JSX.Element {
  const [inGesprek, setInGesprek] = useState(false);

  return inGesprek ? (
    <div className="p-6">
      <BeeldbellenMock client={DEMO_CLIENT} onEnd={() => setInGesprek(false)} />
    </div>
  ) : (
    <StartBeeldbellen
      client={DEMO_CLIENT}
      onGo={() => setInGesprek(true)}
      onBack={() => setInGesprek(false)}
    />
  );
}

function BrandBadge(): JSX.Element {
  const b = useBrand();
  return (
    <span
      className="inline-flex items-center rounded-full px-2 py-0.5 text-[10px] font-semibold text-white"
      style={{ backgroundColor: b.accent }}
    >
      {b.naam}
    </span>
  );
}

/** Nabootsing van het 'Start beeldbellen'-scherm (referentie-ontwerp). */
function StartBeeldbellen({
  client,
  onGo,
  onBack,
}: {
  client: Client;
  onGo: () => void;
  onBack: () => void;
}): JSX.Element {
  const b = useBrand();
  const fieldClass =
    'w-full rounded-md border border-ink-100 px-3 py-2.5 text-[13px] text-ink-700 outline-none focus:border-[color:var(--brand)]';
  return (
    <div className="min-h-0 flex-1 overflow-y-auto">
      <div
        className="relative overflow-hidden px-7 py-6 text-white"
        style={{ backgroundColor: b.banner }}
      >
        <div
          className="absolute inset-y-0 right-0 w-1/3 opacity-20"
          style={{ backgroundColor: '#ffffff', clipPath: 'polygon(40% 0, 100% 0, 100% 100%)' }}
        />
        <h2 className="relative text-[22px] font-bold">Start beeldbellen</h2>
        <p className="relative mt-1 max-w-2xl text-[12.5px] text-white/85">
          Selecteer één of meer cliënten en/of voer een e-mailadres in om een uitnodiging te
          versturen.
        </p>
      </div>

      <div className="grid gap-8 p-7 lg:grid-cols-2">
        <div>
          <h3 className="text-[16px] font-bold" style={{ color: b.banner }}>
            Voor wanneer?
          </h3>
          <div className="mt-3 grid grid-cols-2 gap-3">
            <label className="block">
              <span className="text-[12px] text-ink-500/70">Datum</span>
              <span className="mt-1 flex items-center justify-between rounded-md border border-ink-100 px-3 py-2.5 text-[13px] text-ink-500/50">
                Datum <CalendarIcon size={15} style={{ color: b.accent }} />
              </span>
            </label>
            <label className="block">
              <span className="text-[12px] text-ink-500/70">Tijd</span>
              <span className="mt-1 flex items-center justify-between rounded-md border border-ink-100 px-3 py-2.5 text-[13px] text-ink-500/50">
                Tijd <ClockIcon size={15} style={{ color: b.accent }} />
              </span>
            </label>
          </div>

          <h3 className="mt-6 text-[16px] font-bold" style={{ color: b.banner }}>
            Voor wie?
          </h3>
          <div className="mt-3">
            <span className="text-[13px] font-semibold text-ink-700">Cliënten</span>
            <p className="text-[11px] text-ink-500/55">
              Gebruik het '+' teken om meerdere cliënten toe te voegen
            </p>
            <div className="mt-1 flex items-center justify-between rounded-md border border-ink-100 px-2 py-2">
              <span
                className="inline-flex items-center gap-2 rounded-full px-2.5 py-1 text-[12px] font-medium text-white"
                style={{ backgroundColor: b.accent }}
              >
                {client.naam}
              </span>
              <PlusIcon size={16} style={{ color: b.accent }} />
            </div>
          </div>
          <div className="mt-4">
            <span className="text-[13px] font-semibold text-ink-700">Of e-mail</span>
            <p className="text-[11px] text-ink-500/55">
              Druk op enter om meerdere e-mailadressen toe te voegen
            </p>
            <div className="mt-1 flex items-center justify-between rounded-md border border-ink-100 px-3 py-2.5">
              <span className="text-[13px] text-ink-500/40">naam@voorbeeld.nl</span>
              <PlusIcon size={16} style={{ color: b.accent }} />
            </div>
          </div>
          <div className="mt-4">
            <span className="text-[13px] font-semibold text-ink-700">Bericht</span>
            <p className="text-[11px] text-ink-500/55">
              Optioneel: een persoonlijk bericht dat wordt meegestuurd
            </p>
            <textarea rows={3} placeholder="Type een bericht..." className={`mt-1 ${fieldClass}`} />
          </div>
          <button
            type="button"
            className="mt-5 rounded-md px-4 py-2.5 text-[13px] font-semibold text-white"
            style={{ backgroundColor: b.success }}
          >
            Verstuur uitnodiging
          </button>
        </div>

        <div>
          <h3 className="text-[16px] font-bold" style={{ color: b.banner }}>
            Deel een link
          </h3>
          <div className="mt-3 rounded-lg p-6" style={{ backgroundColor: b.tint }}>
            <div className="flex items-stretch overflow-hidden rounded-md border border-ink-100 bg-white">
              <span className="flex-1 truncate px-3 py-2.5 text-[12px] text-ink-500/70">
                {b.deelUrl}
              </span>
              <button
                type="button"
                className="flex items-center gap-1.5 px-3 text-[12px] font-semibold text-white"
                style={{ backgroundColor: b.success }}
              >
                <CopyIcon size={14} /> Kopieer link
              </button>
            </div>
            <p className="mt-3 text-center text-[11px] text-ink-500/60">
              Wegens privacy-overwegingen wordt de link soms geüpdatet.
            </p>
          </div>
        </div>
      </div>

      <div className="flex items-center justify-center gap-3 border-t border-ink-100 py-4">
        <button
          type="button"
          onClick={onBack}
          className="rounded-md border border-ink-100 px-4 py-2 text-[13px] font-medium text-ink-700 hover:bg-ink-50"
        >
          Terug naar overzicht
        </button>
        <button
          type="button"
          onClick={onGo}
          className="rounded-md px-4 py-2 text-[13px] font-semibold text-white"
          style={{ backgroundColor: b.banner }}
        >
          Ga naar gesprek
        </button>
      </div>
    </div>
  );
}

function BeeldbellenMock({ client, onEnd }: { client: Client; onEnd: () => void }): JSX.Element {
  const b = useBrand();
  return (
    <div>
      <div className="flex items-center gap-2 text-[11px] font-semibold uppercase tracking-wider text-ink-500/55">
        Beeldbellen <BrandBadge /> · {client.naam}
      </div>
      <h3 className="mt-1.5 text-[17px] font-bold text-ink-700">Videoconsult</h3>
      <div className="relative mt-4 flex aspect-video items-center justify-center overflow-hidden rounded-xl bg-ink-700">
        <div className="flex h-16 w-16 items-center justify-center rounded-full bg-white/10 text-2xl font-bold text-white/80">
          {initials(client.naam)}
        </div>
        <div className="absolute bottom-3 right-3 flex h-16 w-24 items-center justify-center rounded-lg border border-white/20 bg-ink-500 text-sm font-semibold text-white/70">
          Jij
        </div>
        <span className="absolute left-3 top-3 inline-flex items-center gap-1.5 rounded-full bg-black/40 px-2.5 py-1 text-[11px] font-medium text-white">
          <ShieldCheckIcon size={13} /> NEN7510 · AES-versleuteld
        </span>
      </div>
      <div className="mt-4 flex items-center justify-center gap-3">
        <span
          className="flex h-11 w-11 items-center justify-center rounded-full"
          style={{ backgroundColor: b.tint, color: b.button }}
        >
          <MicIcon size={18} />
        </span>
        <span
          className="flex h-11 w-11 items-center justify-center rounded-full"
          style={{ backgroundColor: b.tint, color: b.button }}
        >
          <VideoIcon size={18} />
        </span>
        <button
          type="button"
          onClick={onEnd}
          className="flex h-11 w-11 items-center justify-center rounded-full bg-[#c2410c] text-white"
          title="Gesprek beëindigen"
        >
          <PhoneOffIcon size={18} />
        </button>
      </div>
    </div>
  );
}
